import { type HeroTemplateId } from '@/data/heroes';
import { type Npc } from '@/game/npc';
import { type Party, type PartyMember } from '@/game/party';

const DB_NAME = 'knowlages';
const DB_VERSION = 1;
const STORE = 'saves';
const KEY = 'current';

export const CURRENT_SAVE_VERSION = 2 as const;

export interface SaveV2 {
  version: 2;
  party: PartyMember[];
}

export type Save = SaveV2;

const asError = (e: unknown): Error =>
  e instanceof Error ? e : new Error(typeof e === 'string' ? e : 'IDB error');

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (): void => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = (): void => resolve(req.result);
    req.onerror = (): void => reject(asError(req.error));
  });

export const loadSave = async (): Promise<Save | null> => {
  let db: IDBDatabase;
  try {
    db = await openDb();
  } catch {
    return null;
  }
  return new Promise<Save | null>((resolve) => {
    let raw: unknown = null;
    const tx = db.transaction(STORE, 'readonly');
    const store = tx.objectStore(STORE);
    const req = store.get(KEY);
    req.onsuccess = (): void => {
      raw = req.result;
    };
    req.onerror = (): void => {
      raw = null;
    };
    tx.oncomplete = (): void => resolve(validate(raw));
    tx.onerror = (): void => resolve(null);
    tx.onabort = (): void => resolve(null);
  });
};

export const writeSave = async (save: Save): Promise<void> => {
  const db = await openDb();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    store.put(save, KEY);
    tx.oncomplete = (): void => resolve();
    tx.onerror = (): void => reject(asError(tx.error));
    tx.onabort = (): void => reject(asError(tx.error));
  });
};

export const clearSave = async (): Promise<void> => {
  const db = await openDb();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(KEY);
    tx.oncomplete = (): void => resolve();
    tx.onerror = (): void => reject(asError(tx.error));
    tx.onabort = (): void => reject(asError(tx.error));
  });
};

export const buildSave = (party: Party): Save => ({
  version: CURRENT_SAVE_VERSION,
  party: party.recruited.map((m) => ({ ...m })),
});

export const applySave = (save: Save, party: Party, npcs: Npc[]): void => {
  party.recruited = save.party.map((m) => ({ ...m }));
  const recruitedIds = new Set(save.party.map((m) => m.templateId));
  for (const npc of npcs) npc.recruited = recruitedIds.has(npc.templateId);
};

const validate = (raw: unknown): Save | null => {
  if (raw === null || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  if (obj.version !== CURRENT_SAVE_VERSION) return null;
  const partyRaw = obj.party;
  if (!Array.isArray(partyRaw)) return null;

  const party: PartyMember[] = [];
  for (const m of partyRaw as unknown[]) {
    if (m === null || typeof m !== 'object') return null;
    const mo = m as Record<string, unknown>;
    if (typeof mo.templateId !== 'string') return null;
    if (typeof mo.level !== 'number') return null;
    if (typeof mo.hp !== 'number') return null;
    if (typeof mo.maxHp !== 'number') return null;
    party.push({
      templateId: mo.templateId as HeroTemplateId,
      level: mo.level,
      hp: mo.hp,
      maxHp: mo.maxHp,
    });
  }
  return { version: CURRENT_SAVE_VERSION, party };
};
